import type { CSSProperties } from "react";
import { ExternalLink, ImagePlus } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ScrollReveal } from "@/components/motion/ScrollReveal";
import { cn } from "@/lib/utils";
import {
  filledSponsorTiers,
  sponsorTiers,
  stripSponsorTiers,
  type Sponsor,
  type SponsorTier,
  type SponsorTierId
} from "./data";

/*
 * Sponsor logo wall.
 *
 * Tier tone is carried by a DECORATIVE BAR ONLY — never by the heading text.
 * Measured against the white tile, the celebration accents fail AA badly
 * (accent-orange ~2.3:1, accent-teal ~2.4:1, accent ~1.9:1). PackagesGrid gets
 * away with tinted text because its tone sits on a tiny eyebrow beside a black
 * card title; here the tier name IS the content, so it stays text-foreground.
 *
 * Tailwind needs literal class names, so every tone/size string is spelled out
 * rather than templated (same convention as SponsorshipPage / PackagesGrid).
 */
const TIER_TONES: Record<SponsorTierId, { bar: string }> = {
  // The event's owners → Bursa navy, the theme's secondary and what
  // SponsorshipPage's INKIND_TONE already uses for "outside the tier palette".
  organisers: { bar: "bg-secondary" },
  // Naming right, not a rung on the paid ladder → brand red, which is what the
  // codebase already uses for "outside the tier palette".
  officialBank: { bar: "bg-primary" },
  // The four below mirror PackagesGrid's TIER_TONES exactly so the two pages agree.
  platinum: { bar: "bg-accent" },
  gold: { bar: "bg-accent-magenta" },
  silver: { bar: "bg-accent-teal" },
  bronze: { bar: "bg-accent-orange" },
  supporting: { bar: "bg-accent-violet" }
};

/*
 * Show the unsold ladder tiers as dashed "slot available" tiles linking to
 * /sponsorship. OFF by default — an empty Platinum row on a credibility page
 * reads as a vacancy, not an invitation. Flip to true if the client wants the
 * open tiers advertised.
 */
const SHOW_EMPTY_TIER_SLOTS = false;

type TileSize = "standard" | "compact";

/**
 * Outer width — drives how many logos land per row at each breakpoint.
 *
 * Every tier renders at `standard`: the wall states who backs the event, and
 * sizing one logo larger than another is a claim about relative billing that
 * the tier headings already make in words.
 */
export const TILE_WIDTH: Record<TileSize, string> = {
  standard: "flex w-[9.5rem] sm:w-[11rem] md:w-[13rem] lg:w-[14rem]",
  /*
   * The footer strip carries its groups on every page. That row is static and
   * finite: it centres while it fits and turns into a plain horizontal scroller
   * once it doesn't, so this width trades logo size against how many sponsors
   * land on screen before anyone has to scroll.
   *
   * At 10rem the current five fit un-scrolled at 1024 and up (track 888px); it
   * scrolls on phones and, deliberately, at iPad portrait (736px available).
   * That was the accepted cost of making the footer logos ~2x bigger — the
   * scroller is a designed, working state, not a fallback.
   */
  compact: "flex w-[7rem] sm:w-[7.5rem] md:w-[10rem]"
};

/**
 * Inner box height + padding.
 *
 * Padding is deliberately tight: it is the ONLY thing standing between the logo
 * and a bigger render. See LOGO_FILL — max logo area is contentWidth² / rMax, so
 * every pixel of horizontal padding costs size on both surfaces.
 *
 * Keep in step with TILE_GEOM below, which restates these numbers so the scale
 * can be computed. Tailwind needs literal classes, hence the duplication.
 */
const TILE_BOX: Record<TileSize, string> = {
  standard: "h-24 px-2 py-2 md:h-28",
  compact: "h-16 px-1 py-1 md:h-20"
};

/** TILE_BASE carries `border`, which eats 1px off each edge of the content box. */
const TILE_BORDER = 1;

type Band = "base" | "sm" | "md" | "lg";

/**
 * The numeric form of TILE_WIDTH + TILE_BOX, in px. One fact in two forms — the
 * classes are what render, this is what the scale is derived from, so THEY MUST
 * BE UPDATED TOGETHER. (Tailwind can't consume computed values and JS can't read
 * a class, so neither can be generated from the other.)
 */
const TILE_GEOM: Record<TileSize, Record<Band, { w: number; h: number; px: number; py: number }>> = {
  standard: {
    base: { w: 152, h: 96, px: 8, py: 8 }, //     w-[9.5rem] h-24 px-2 py-2
    sm: { w: 176, h: 96, px: 8, py: 8 }, //     sm:w-[11rem]
    md: { w: 208, h: 112, px: 8, py: 8 }, //     md:w-[13rem] md:h-28
    lg: { w: 224, h: 112, px: 8, py: 8 } //     lg:w-[14rem]
  },
  compact: {
    base: { w: 112, h: 64, px: 4, py: 4 }, //       w-[7rem] h-16 px-1 py-1
    sm: { w: 120, h: 64, px: 4, py: 4 }, //     sm:w-[7.5rem]
    md: { w: 160, h: 80, px: 4, py: 4 }, //     md:w-[10rem] md:h-20
    lg: { w: 160, h: 80, px: 4, py: 4 } //     (no lg override — md carries on)
  }
};

/*
 * EQUAL OPTICAL AREA — why the logos are not all the same height.
 *
 * A 5.3:1 wordmark (Vistra) and a 2.1:1 badge (DCD) cannot share a height and
 * look equal; the wordmark would tower over everything. What reads as "the same
 * size" is equal AREA, so that is what this computes.
 *
 * Give the logo a max-height of `k / sqrt(ratio)` as a PERCENTAGE of the tile's
 * content height H. Rendered area is then
 *     (p*H)^2 * ratio  =  (k/sqrt(r) * H)^2 * r  =  k^2 * H^2
 * — independent of ratio, and, because p is a percentage, automatically equal
 * at every breakpoint with no per-breakpoint px math.
 *
 * k is capped by the tile's own content aspect ratio, since the WIDEST logo must
 * still fit the width:  k <= (W/H) / sqrt(rMax). Push k past that and the widest
 * marks clamp on max-w-full instead, which breaks the equality — the wall goes
 * visibly uneven again.
 *
 * NOTHING HERE IS HAND-TUNED. rMax comes from the sponsor data and the caps come
 * from TILE_GEOM, so adding a sponsor of ANY aspect ratio re-derives every k, at
 * every breakpoint, on both surfaces, automatically. That is deliberate: the
 * previous version hardcoded numbers against Vistra being the widest logo at
 * 5.34:1, which would have silently broken on the next wider sponsor. Adding a
 * logo stays a one-object-literal edit in data.ts.
 *
 * k is per breakpoint BAND because TILE_WIDTH and TILE_BOX change at different
 * breakpoints, so the tile's proportions (and therefore the cap) differ. Note md
 * is TIGHTER than sm for the standard tile — it grows taller but barely wider —
 * which is why sm and md need separate bands rather than sharing one.
 *
 * THIS ONLY HOLDS IF EVERY FILE IS CROPPED TO ITS INK BOUNDS. The ratio is read
 * from the file, so baked-in whitespace is indistinguishable from the mark and
 * silently shrinks it. See the crop note at the top of data.ts.
 */

/** Widest aspect ratio in a set of tiers; 1 if the set is empty. */
function maxRatio(tiers: SponsorTier[]): number {
  const r = Math.max(...tiers.flatMap((t) => t.sponsors).map((s) => s.width / s.height));
  return Number.isFinite(r) && r > 0 ? r : 1;
}

/**
 * Widest logo actually rendered on each surface. Scoped per surface, not global:
 * the strip omits the organisers (see stripSponsorTiers), so a very wide
 * organiser logo must not shrink the footer.
 */
const R_MAX: Record<TileSize, number> = {
  standard: maxRatio(filledSponsorTiers),
  compact: maxRatio(stripSponsorTiers)
};

/**
 * How much of the geometric maximum to use. THE tuning knob — the one number to
 * change if the logos should be bigger or smaller overall.
 *   1.00 = the widest logo exactly spans the content box edge to edge
 *   0.99 = a hair of slack, so subpixel rounding can never trip a width clamp
 *
 * A clamp is not cosmetic: it is what breaks equal area, so if a logo ever
 * renders width-clamped, LOWER THIS (0.98, then 0.97) rather than living with an
 * uneven wall. Verify by measuring rendered area in-browser — spread must be
 * 1.00x.
 *
 * It also keeps logos clear of the ExternalLink icon on standard tiles, and it
 * is worth knowing WHY that is safe for any future logo rather than luck: the
 * icon sits in the bottom-right 22px, so overlapping it needs a logo that is
 * BOTH wide (w >= tileW - 44) and tall (h >= tileH - 44) — i.e. area >= 180x68 =
 * 12,240px2 at lg. Equal area pins w*h at ~7,800, well under that, so no aspect
 * ratio can reach the icon. Only a large LOGO_FILL increase could change that.
 */
const LOGO_FILL = 0.99;

/** Equal-area scale for one tile size at one breakpoint band. */
function scaleFor(size: TileSize, band: Band): number {
  const g = TILE_GEOM[size][band];
  const w = g.w - 2 * g.px - 2 * TILE_BORDER;
  const h = g.h - 2 * g.py - 2 * TILE_BORDER;
  // min(1, …) so a very short/wide tile can't push the logo past its own box height.
  return Math.min(1, w / h / Math.sqrt(R_MAX[size])) * LOGO_FILL;
}

/*
 * Equal area means the widest logo sets the pace for everyone. That is correct —
 * it is what keeps the set even — but it means a much wider mark legitimately
 * shrinks the whole wall, which is surprising if you only added one sponsor.
 * Say so in dev rather than let someone hunt for it.
 */
if (import.meta.env.DEV) {
  const DESIGN_R_MAX = 5.5; // Vistra, the widest logo the current sizing was reviewed against
  for (const [size, r] of Object.entries(R_MAX)) {
    if (r > DESIGN_R_MAX) {
      console.warn(
        `[SponsorWall] Widest ${size} logo is now ${r.toFixed(2)}:1 (was designed around ` +
          `${DESIGN_R_MAX}:1), so every logo on that surface is rendering smaller to stay even ` +
          `with it. Options: re-crop or use a stacked lockup for that sponsor, give it an ` +
          `opticalScale in data.ts, or accept the smaller set.`
      );
    }
  }
}

/*
 * Emitted as CSS custom properties rather than one maxHeight, because the value
 * has to change at breakpoints and inline styles can't hold media queries. The
 * consuming classes (max-h-[var(--logo-mh)] etc.) are static strings, so
 * Tailwind still generates them — an interpolated arbitrary value would not.
 */
function logoScaleVars(sponsor: Sponsor, size: TileSize): CSSProperties {
  const ratio = sponsor.width / sponsor.height;
  const nudge = sponsor.opticalScale ?? 1;
  const pct = (band: Band) =>
    `${((scaleFor(size, band) / Math.sqrt(ratio)) * nudge * 100).toFixed(2)}%`;
  return {
    "--logo-mh": pct("base"),
    "--logo-mh-sm": pct("sm"),
    "--logo-mh-md": pct("md"),
    "--logo-mh-lg": pct("lg")
  } as CSSProperties;
}

/*
 * bg-card is pure white and is MANDATORY here: the UOB artwork carries a hard
 * white matte, which would read as a white rectangle floating on the page's
 * pale-blue background. A white tile makes the matted logo and the transparent
 * PNGs sit identically. Same reasoning as the white logo panel in Footer.tsx.
 *
 * rounded-xl + border + shadow-sm is the shadcn Card recipe, hand-written
 * because this must be an <a>, not a <div> — exactly what PathCard in
 * SponsorshipPage.tsx does for the same reason.
 */
const TILE_BASE =
  "group relative flex w-full items-center justify-center rounded-xl border bg-card shadow-sm " +
  "transition-[box-shadow,border-color,transform] duration-200 " +
  "hover:border-primary/30 hover:shadow-md motion-safe:hover:-translate-y-0.5 " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
  "focus-visible:ring-offset-2 focus-visible:ring-offset-background";

export function SponsorTile({
  sponsor,
  size = "standard",
  eager = false
}: {
  sponsor: Sponsor;
  size?: TileSize;
  eager?: boolean;
}) {
  const { t } = useTranslation("sponsors");

  const logo = (
    <img
      src={sponsor.logo}
      alt={sponsor.name}
      width={sponsor.width}
      height={sponsor.height}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      /*
       * Inline, not aspect-[w/h] / max-h-[x%] classes: both values are computed
       * from data and Tailwind arbitrary values can't be templated.
       *
       * aspectRatio is required, not cosmetic — Header.tsx documents the Safari
       * flexbox <img> bug where a wide image renders at near-natural size and
       * blows up the layout, and these tiles are flex containers with wide
       * <img> children. maxHeight is what equalises optical area (see
       * LOGO_SCALE); max-w-full below is the backstop that clamps the widest
       * marks on narrow screens.
       */
      style={{
        aspectRatio: `${sponsor.width} / ${sponsor.height}`,
        ...logoScaleVars(sponsor, size)
      }}
      className="w-auto max-w-full object-contain max-h-[var(--logo-mh)] sm:max-h-[var(--logo-mh-sm)] md:max-h-[var(--logo-mh-md)] lg:max-h-[var(--logo-mh-lg)]"
    />
  );

  return (
    <a
      href={sponsor.href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(TILE_BASE, TILE_BOX[size])}
    >
      {logo}
      {/* The alt text is the link's accessible name; this appends the new-tab
          warning to it (WCAG G201). No aria-label on the <a> — it would
          override the alt and leave the image unnamed. */}
      <span className="sr-only"> {t("logo.opensInNewTab")}</span>
      {/* Always visible, not hover-only: a hover-only affordance is invisible
          to keyboard and touch users. */}
      {size !== "compact" && (
        <ExternalLink
          aria-hidden
          className="pointer-events-none absolute bottom-2 right-2 h-3.5 w-3.5 text-muted-foreground/40 transition-colors duration-200 group-hover:text-primary"
        />
      )}
    </a>
  );
}

function TierHeading({ tier }: { tier: SponsorTier }) {
  const { t } = useTranslation("sponsors");
  return (
    <ScrollReveal className="flex flex-col items-center text-center">
      <span aria-hidden className={cn("h-1 w-12 rounded-full", TIER_TONES[tier.id].bar)} />
      <h2 className="mt-4 text-2xl md:text-3xl">{t(`tiers.${tier.id}`)}</h2>
    </ScrollReveal>
  );
}

function EmptySlot() {
  const { t } = useTranslation("sponsors");
  return (
    <Link
      to="/sponsorship/"
      className="grid h-24 w-full place-items-center rounded-xl border-2 border-dashed border-border bg-muted/40 px-5 py-4 text-center transition-colors duration-200 hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:h-28"
    >
      <span>
        <ImagePlus aria-hidden className="mx-auto h-6 w-6 text-foreground/40" />
        <span className="mt-2 block text-sm text-muted-foreground">{t("emptySlot.title")}</span>
      </span>
    </Link>
  );
}

export function SponsorWall() {
  const { t } = useTranslation("sponsors");
  const tiers = SHOW_EMPTY_TIER_SLOTS ? sponsorTiers : filledSponsorTiers;

  // Fresh year, nothing announced yet — say so rather than render a hero, a
  // hole, then a CTA.
  if (tiers.length === 0) {
    return (
      <section className="container py-16 md:py-20">
        <ScrollReveal className="mx-auto max-w-xl text-center">
          <h2 className="text-2xl md:text-3xl">{t("pending.title")}</h2>
          <p className="mt-3 text-muted-foreground">{t("pending.body")}</p>
        </ScrollReveal>
      </section>
    );
  }

  return (
    <section className="container py-16 md:py-20">
      {tiers.map((tier, index) => {
        const tierLabel = t(`tiers.${tier.id}`);

        return (
          <div
            key={tier.id}
            /* A rule between tiers, mirroring the reference wall. Not the
               alternating bg-muted/30 band idiom — with up to six tiers that
               zebra-stripes the whole page. */
            className={
              index === 0 ? undefined : "mt-14 border-t border-border pt-14 md:mt-16 md:pt-16"
            }
          >
            <TierHeading tier={tier} />

            {/* Centred flex-wrap rather than a grid: a tier may hold one logo
                or eight, and a grid leaves a lone logo hard-left and a partial
                row with a hole. Wrapping centres every row at any count. */}
            <ScrollReveal.Group
              role="list"
              aria-label={tierLabel}
              className="mt-8 flex flex-wrap items-stretch justify-center gap-4 md:gap-6"
              stagger={0.07}
            >
              {tier.sponsors.map((sponsor) => (
                <ScrollReveal.Item
                  key={sponsor.id}
                  role="listitem"
                  className={TILE_WIDTH.standard}
                >
                  <SponsorTile sponsor={sponsor} eager={index === 0} />
                </ScrollReveal.Item>
              ))}

              {SHOW_EMPTY_TIER_SLOTS && tier.sponsors.length === 0 && (
                <ScrollReveal.Item role="listitem" className={TILE_WIDTH.standard}>
                  <EmptySlot />
                </ScrollReveal.Item>
              )}
            </ScrollReveal.Group>
          </div>
        );
      })}
    </section>
  );
}
